import { z } from 'zod';
import { DeepReadonly } from '../utils/utilTypes';
import {
  ValueMappingAutomapperSuggestionZod,
  ValueMappingConfig,
} from './valueMappingTypes';
import {
  CUSTOMIZATION_MAPPING_CONFIG_IDS,
  MethodologyCustomizationMappingConfigId,
  METHODOLOGY_CUSTOMIZATION_REQUIRED_CONTEXT_COLUMNS,
  METHODOLOGY_CUSTOMIZATION_REQUIRED_OUTPUT_COLUMNS,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_IDENTIFIER,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_NAME,
  METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_SUPPLIER_NAME,
} from '../methodologyCustomization/methodologyCustomizationConstants';
import { sqlIdentifier } from '../sqlTemplate';

// ID columns
export const GROUP_ID_COL = '__group_id';
export const GROUP_ID_COL_I = sqlIdentifier(GROUP_ID_COL);
export const GROUP_ID_PREFIX = 'gid_1';

// Special columns
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS =
  'user_upload_fmds';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_END_DATE = 'end_date';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_START_DATE = 'start_date';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY =
  'vendor_entity';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_SUGGESTIONS = 'suggestions';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE =
  'should_exclude';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_DATASET_KIND = 'dataset_kind';

// Changelog columns
export const VALUE_MAPPING_COLUMN_NAME_IS_MIGRATION_ENTRY =
  'is_migration_entry';
export const VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_USER_NAME =
  'edited_user_name';
export const VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_USER_ID = 'edited_user_id';
export const VALUE_MAPPING_COLUMN_NAME_LAST_EDITED_TIME = 'edited_time';
export const VALUE_MAPPING_SPECIAL_COLUMN_NAME_FLAGGED_ROW = 'row_flag';

// Config key columns
export const VALUE_MAPPING_FINANCIALS_SCHEMA_KEY_COLUMN_ACCOUNT_ID =
  'financial_account_id';
export const VALUE_MAPPING_SUPPLIER_COMPANY_SCHEMA_KEY_COLUMN_SUPPLIER_NAME =
  'vendor_entity';

// Config context columns
export const VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME =
  'financial_account_name';
export const VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_DESCRIPTION =
  'financial_account_description';
export const VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND =
  'amount_spend';

// Config output columns
export const VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA = 'bea';
export const VALUE_MAPPING_OUTPUT_COLUMN_NAME_FMD = 'fmds';
export const VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID = 'company_id';

// Config id's
export const FINANCIALS_CONFIG_ID = 'v0_financial_account';
export const SUPPLIER_COMPANY_CONFIG_ID = 'v0_supplier_company';
export const DEFAULT_CONFIG_INITIAL_VERSION_ID = 'initial_version';

// Enum of all value mapping config IDs, including methodology customization
export const VALUE_MAPPING_CONFIG_IDS = [
  FINANCIALS_CONFIG_ID,
  SUPPLIER_COMPANY_CONFIG_ID,
  ...CUSTOMIZATION_MAPPING_CONFIG_IDS,
] as const;

export type ValueMappingConfigId = (typeof VALUE_MAPPING_CONFIG_IDS)[number];
export function isValueMappingConfigId(
  input: string
): input is ValueMappingConfigId {
  return VALUE_MAPPING_CONFIG_IDS.includes(input as any);
}

// Config names
export const FINANCIALS_CONFIG_NAME = 'financials_account_bea';
export const SUPPLIER_COMPANY_CONFIG_NAME = 'supplier_company';

// Methodology customization mappings go through a separate flow
export function isNonMethodologyCustomizationConfigId(
  value: string | null
): value is Exclude<
  ValueMappingConfigId,
  MethodologyCustomizationMappingConfigId
> {
  return (
    VALUE_MAPPING_CONFIG_IDS.includes(value as ValueMappingConfigId) &&
    !CUSTOMIZATION_MAPPING_CONFIG_IDS.includes(
      value as MethodologyCustomizationMappingConfigId
    )
  );
}

/**
 * Typeguard for converting an arbitrary string to a ValueMappingRuleId if it's one of the values.
 */
export function isMappingKindConfigId(
  value: string | null
): value is ValueMappingConfigId {
  return VALUE_MAPPING_CONFIG_IDS.includes(value as ValueMappingConfigId);
}

// TODO: move all of ValueMappingPageConfiguration and these configs together
export const valueMappingFinAutomapperSuggestionsZodSchema = z.object({
  [VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA]: ValueMappingAutomapperSuggestionZod,
});

export const valueMappingUserUploadsZodSchema = z.array(z.string()).nullable();

export type ValueMappingUserUploadsZod = z.infer<
  typeof valueMappingUserUploadsZodSchema
>;

/**
 * NOTE: Try not to use this!!! Please use ValueMappingService.getValueMappingConfigByIdAndVersion if you can.
 * These are hard coded for v0 and need to be copied into value_mapping_context.py for the db constraint.
 */
export const HardCodedValueMappingIdToVersionToConfig: DeepReadonly<
  Record<
    ValueMappingConfigId,
    Record<string, ValueMappingConfig> // version id : config
  >
> = {
  [FINANCIALS_CONFIG_ID]: {
    initial_version: {
      _type: 'simple',
      name: FINANCIALS_CONFIG_NAME,
      liveAsOf: '2024-01-01.1',
      versionId: DEFAULT_CONFIG_INITIAL_VERSION_ID,
      versionDescription:
        'Initial financial config before versions are formally implemented in value mappings configs.',
      keyColumns: {
        [VALUE_MAPPING_FINANCIALS_SCHEMA_KEY_COLUMN_ACCOUNT_ID]: {
          type: 'string',
          description:
            'Unique numerical identifier of an account. If empty, the account name will be used as an identifier.',
          isRequired: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_START_DATE]: {
          type: 'string',
          description:
            'Start date, inclusive, of activity data this mapping applies to.',
          format: 'date',
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_END_DATE]: {
          type: 'string',
          description:
            'End date, exclusive, of activity data this mapping applies to.',
          format: 'date',
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_VENDOR_OVERRIDE_ENTITY]: {
          type: 'string',
          description: 'A specific vendor Account ID has spend on.',
          isFallback: true,
        },
      },
      contextColumns: {
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS]: {
          type: 'string',
          description: 'The user uploads that the record was imported from.',
          zod: valueMappingUserUploadsZodSchema,
          isSet: true,
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_NAME]: {
          type: 'string',
          description: 'The descriptive name of the financial account.',
        },
        [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_ACCOUNT_DESCRIPTION]: {
          type: 'string',
          description:
            'Description of the cost, containing keywords such as "adjustments", "write-off", "reclass" that indicate whether the cost is associated with cash out the door at the time of purchase. This information may be recorded under different headers depending on the company.',
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND]: {
          type: 'number',
          description:
            'This is an approximate number. It aggregates spend across financial datasets, for all upload time periods. It is approximate due to currency conversion. We recommend using this number to inform order of magnitude when prioritizing your mappings.',
          isCurrency: true,
          isCumulative: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_SUGGESTIONS]: {
          type: 'string',
          description:
            'JSON containing automapper suggestions and explanations.',
          zod: valueMappingFinAutomapperSuggestionsZodSchema,
          isHiddenByDefault: true,
        },
      },
      outputColumns: {
        [VALUE_MAPPING_OUTPUT_COLUMN_NAME_BEA]: {
          type: 'string',
          description:
            'These are standard industry codes from the Bureau of Economic Analysis (BEA). We will use these mappings to calculate your carbon footprint.',
          isRequiredForPublish: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_SHOULD_EXCLUDE]: {
          type: 'boolean',
          description:
            'Indicates that activity from the account should be included in footprints.',
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_FLAGGED_ROW]: {
          type: 'string', // enum
          description: 'Row is marked for further work or review.',
          isHiddenByDefault: true,
        },
      },
    },
  },
  [SUPPLIER_COMPANY_CONFIG_ID]: {
    initial_version: {
      _type: 'simple',
      name: SUPPLIER_COMPANY_CONFIG_NAME,
      liveAsOf: '2024-01-01.1',
      versionId: DEFAULT_CONFIG_INITIAL_VERSION_ID,
      versionDescription:
        'Initial supplier company config before versions are formally implemented in value mappings configs.',
      keyColumns: {
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_START_DATE]: {
          type: 'string',
          description:
            'Start date, inclusive, of activity data this mapping applies to.',
          format: 'date',
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_END_DATE]: {
          type: 'string',
          description:
            'End date, exclusive, of activity data this mapping applies to.',
          format: 'date',
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_SUPPLIER_COMPANY_SCHEMA_KEY_COLUMN_SUPPLIER_NAME]: {
          type: 'string',
          description: 'Name of supplier as it shows up in your dataset',
        },
      },
      contextColumns: {
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_USER_UPLOAD_FMDS]: {
          type: 'string',
          description: 'The user uploads that the record was imported from.',
          zod: valueMappingUserUploadsZodSchema,
          isSet: true,
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_DATASET_KIND]: {
          type: 'string',
          description:
            'The datasets that associated user uploads were imported to.',
          zod: valueMappingUserUploadsZodSchema,
          isSet: true,
          isHiddenByDefault: true,
        },
        [VALUE_MAPPING_FINANCIALS_SCHEMA_CONTEXT_COLUMN_AMOUNT_SPEND]: {
          type: 'number',
          description:
            'This is an approximate number. It aggregates spend across financial datasets, for all upload time periods. It is approximate due to currency conversion. We recommend using this number to inform order of magnitude when prioritizing your mappings.',
          isCurrency: true,
          isCumulative: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_SUGGESTIONS]: {
          type: 'string',
          description:
            'JSON containing automapper suggestions and explanations.',
          zod: valueMappingFinAutomapperSuggestionsZodSchema,
          isHiddenByDefault: true,
        },
      },
      outputColumns: {
        [VALUE_MAPPING_OUTPUT_COLUMN_NAME_COMPANY_ID]: {
          type: 'string',
          description: 'Company that supplier corresponds to',
          isRequiredForPublish: true,
        },
        [VALUE_MAPPING_SPECIAL_COLUMN_NAME_FLAGGED_ROW]: {
          type: 'string', // enum
          description: 'Row is marked for further work or review.',
          isHiddenByDefault: true,
        },
      },
    },
  },
  electricity_production_ef_building: {
    initial_version: {
      _type: 'simple',
      name: 'electricity_production_ef_building',
      liveAsOf: '2024-01-01.1',
      versionId: DEFAULT_CONFIG_INITIAL_VERSION_ID,
      versionDescription:
        'Initial methodology config before versions are formally implemented in value mappings configs.',
      keyColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_IDENTIFIER]: {
          type: 'string',
          description: 'Building unique identifier',
          isRequired: true,
          isHiddenByDefault: true,
        },
      },
      contextColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_NAME]: {
          type: 'string',
          description: 'Building name',
        },
        ...METHODOLOGY_CUSTOMIZATION_REQUIRED_CONTEXT_COLUMNS,
      },
      outputColumns: METHODOLOGY_CUSTOMIZATION_REQUIRED_OUTPUT_COLUMNS,
    },
  },
  electricity_t_d_wtt_building: {
    initial_version: {
      _type: 'simple',
      name: 'electricity_t_d_wtt_building',
      liveAsOf: '2024-01-01.1',
      versionId: DEFAULT_CONFIG_INITIAL_VERSION_ID,
      versionDescription:
        'Initial methodology config before versions are formally implemented in value mappings configs.',
      keyColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_IDENTIFIER]: {
          type: 'string',
          description: 'Building unique identifier',
          isRequired: true,
          isHiddenByDefault: true,
        },
      },
      contextColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_NAME]: {
          type: 'string',
          description: 'Building name',
        },
        ...METHODOLOGY_CUSTOMIZATION_REQUIRED_CONTEXT_COLUMNS,
      },
      outputColumns: METHODOLOGY_CUSTOMIZATION_REQUIRED_OUTPUT_COLUMNS,
    },
  },
  electricity_wtt_ef_building: {
    initial_version: {
      _type: 'simple',
      name: 'electricity_wtt_ef_building',
      liveAsOf: '2024-01-01.1',
      versionId: DEFAULT_CONFIG_INITIAL_VERSION_ID,
      versionDescription:
        'Initial methodology config before versions are formally implemented in value mappings configs.',
      keyColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_IDENTIFIER]: {
          type: 'string',
          description: 'Building unique identifier',
          isRequired: true,
          isHiddenByDefault: true,
        },
      },
      contextColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_BUILDING_NAME]: {
          type: 'string',
          description: 'Building name',
        },
        ...METHODOLOGY_CUSTOMIZATION_REQUIRED_CONTEXT_COLUMNS,
      },
      outputColumns: METHODOLOGY_CUSTOMIZATION_REQUIRED_OUTPUT_COLUMNS,
    },
  },
  supplier_spend_ef: {
    initial_version: {
      _type: 'simple',
      name: 'supplier_spend_ef',
      liveAsOf: '2024-01-01.1',
      versionId: DEFAULT_CONFIG_INITIAL_VERSION_ID,
      versionDescription:
        'Initial methodology config before versions are formally implemented in value mappings configs.',
      keyColumns: {
        [METHODOLOGY_CUSTOMIZATION_SPECIAL_COLUMN_NAME_SUPPLIER_NAME]: {
          type: 'string',
          description: 'Supplier name',
          isRequired: true,
        },
      },
      contextColumns: {
        ...METHODOLOGY_CUSTOMIZATION_REQUIRED_CONTEXT_COLUMNS,
      },
      outputColumns: METHODOLOGY_CUSTOMIZATION_REQUIRED_OUTPUT_COLUMNS,
    },
  },
};
